import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import {
  Warning,
  Payment,
  Schedule,
  CheckCircleOutline,
} from "@mui/icons-material";
import { FormatFullName } from "app/utils/helper";
import { useSelector } from "react-redux";
import { selectSelectedSchool } from "app/modules/schools/selectors";
import { THEME } from "app/config/constants";
import { useNavigate } from "react-router-dom";
import appRoutes from "app/config/routes";
import DialogHeader from "app/components/common/DialogHeader";
import dayjs from "dayjs";

const DueServiceChargePopup = ({ open, onClose }) => {
  const schoolDetails = useSelector(selectSelectedSchool);
  const navigate = useNavigate();
  const dueDate = dayjs(schoolDetails?.upcomingDueDate);

  const handlePayNowClick = () => {
    navigate(`/${appRoutes.schoolProfile}?tab=transaction`);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: THEME.borderRadius,
            boxShadow: THEME.boxShadowModern,
          },
        }}
      >
        <DialogHeader title="Payment Reminder" handleClose={onClose} />

        <DialogContent sx={{ pt: 2, pb: 2, px: 2 }}>
          <Stack spacing={2}>
            {/* Alert Banner */}
            <Card
              elevation={0}
              sx={{
                background: "linear-gradient(135deg, #FFF4E6 0%, #FFE6E6 100%)",
                border: "1px solid #FFB84D",
                borderRadius: THEME.borderRadius,
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Warning sx={{ color: "#FF6B35", fontSize: 40 }} />
                  <Box flex={1}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: THEME.primary,
                      }}
                    >
                      Service Charge Payment Due
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: THEME.primary, opacity: 0.8 }}
                    >
                      Your service payment is overdue. Please settle your
                      account to continue uninterrupted service.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* User Info */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: THEME.primary,
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Dear{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: THEME.secondary }}
                >
                  {FormatFullName(
                    schoolDetails?.userDetail?.firstName,
                    schoolDetails?.userDetail?.middleName,
                    schoolDetails?.userDetail?.lastName
                  )}
                </Box>
                ,
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: THEME.primary, opacity: 0.8 }}
              >
                We love having you in the EkalSutra family! To keep enjoying our
                ERP magic running smoothly, please clear your pending service
                charges. Think of it as fueling our servers.
              </Typography>
            </Box>

            {/* Payment Info Card */}
            <Card
              variant="outlined"
              sx={{
                borderColor: THEME.secondary,
                borderRadius: THEME.borderRadius,
                background: "rgba(39, 137, 253, 0.03)",
              }}
            >
              <CardContent sx={{ p: 1 }}>
                <Stack direction="column" spacing={0.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Schedule sx={{ color: THEME.secondary, fontSize: 20 }} />
                      <Typography
                        variant="body1"
                        sx={{ color: THEME.primary, fontWeight: 600 }}
                      >
                        Payment Due Date
                      </Typography>
                    </Stack>
                    <Chip
                      label={dueDate.format("DD MMM, YYYY")}
                      color="error"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems={"center"}>
                    <Payment sx={{ color: THEME.secondary, fontSize: 20 }} />
                    <Typography variant="body1" sx={{ color: THEME.primary }}>
                      To view your pending charges and make payment, please
                      click on the "Make Payment" button below.
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Benefits of Timely Payment */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: THEME.primary,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Benefits of Timely Payment:
              </Typography>
              <Stack spacing={0.8}>
                {[
                  "Uninterrupted access to all ERP features",
                  "Priority customer support",
                  "Regular updates and new features",
                  "Secure data backup and storage",
                ].map((benefit, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CheckCircleOutline
                      sx={{ color: "#4CAF50", fontSize: 18 }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ color: THEME.primary, opacity: 0.8 }}
                    >
                      {benefit}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Contact Info */}
            <Box
              sx={{
                background: "#F5F7FA",
                borderRadius: THEME.borderRadiusSmall,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: THEME.primary, opacity: 0.7 }}
              >
                For any queries, contact us at:{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: THEME.secondary }}
                >
                  support@ekalsutra.com
                </Box>{" "}
                or call{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: THEME.secondary }}
                >
                  08069328986
                </Box>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{
              textTransform: "capitalize",
              borderRadius: "8px",
              px: 3,
            }}
          >
            Remind Me Later
          </Button>

          <Button
            variant="contained"
            size="large"
            color="secondary"
            onClick={handlePayNowClick}
            startIcon={<Payment />}
            sx={{
              textTransform: "capitalize",
              borderRadius: "8px",
              px: 3,
              fontWeight: 600,
            }}
          >
            Make Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DueServiceChargePopup;
